import { useEffect, useMemo, useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import AccountSelect from '../../components/AccountSelect';
import { reportsApi, type AccountStatementDto } from '../../api/reports';
import { personsApi } from '../../api/persons';
import type { PersonDto } from '../../types/person';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';
import { formatMoney } from '../../utils/formatMoney';

/**
 * SĄSKAITŲ IŠRAŠAS — the per-account ledger extract: one row per GL line
 * (sub-accounts included), correspondence column = the same entry's other
 * accounts, footer trio opening/turnover/closing in the D/C split.
 */
export default function AccountStatement() {
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [account, setAccount] = useState<number | null>(null);
  const [personId, setPersonId] = useState<number | null>(null);
  const [persons, setPersons] = useState<PersonDto[]>([]);
  const [data, setData] = useState<AccountStatementDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    personsApi
      .list({ limit: 200 })
      .then(setPersons)
      .catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!account) {
      setError('Pasirinkite sąskaitą.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      setData(await reportsApi.accountStatement({ from, to, account, personId: personId ?? undefined }));
    } catch (e: any) {
      setError(e.message || 'Nepavyko sugeneruoti sąskaitų išrašo');
    } finally {
      setLoading(false);
    }
  };

  const handleCsv = async () => {
    if (!account) return;
    try {
      await reportsApi.accountStatementCsv({ from, to, account, personId: personId ?? undefined });
    } catch (e: any) {
      setError(e.message || 'Eksportas nepavyko');
    }
  };

  const recordCount = data?.rows.length ?? 0;
  const screenPreview = useMemo(
    () =>
      (data?.rows ?? []).slice(0, 10).map(r =>
        `${r.date} ${r.documentNumber}: D ${formatMoney(r.debit)} / K ${formatMoney(r.credit)} ↔ ${r.correspondence}`.substring(
          0,
          120,
        ),
      ),
    [data],
  );

  useReportScreen(
    data
      ? `Sąskaitų išrašas (${data.account}, ${data.from}–${data.to}): apyvarta D ${formatMoney(data.debitTurnover)} / K ${formatMoney(data.creditTurnover)}, likutis ${formatMoney(data.debitBalanceEnd - data.creditBalanceEnd)}`
      : 'Sąskaitų išrašas: nesugeneruotas',
    {
      recordCount,
      filters: { nuo: from, iki: to, ...(account != null && { saskaita: String(account) }), ...(personId != null && { asmuo: String(personId) }) },
      preview: screenPreview,
    },
  );

  return (
    <div className="space-y-4">
      <ReportsNav />
      <h1 className="print:hidden">Sąskaitų išrašas</h1>

      <div
        className="filters print:hidden"
        style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}
      >
        <label>
          Nuo: <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label>
          Iki: <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <label>
          Sąskaita: <AccountSelect value={account} onChange={setAccount} />
        </label>
        <label>
          Asmuo:
          <select value={personId ?? ''} onChange={e => setPersonId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">— visi —</option>
            {persons.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <button onClick={handleGenerate} disabled={loading || !account}>
          {loading ? <LoadingSpinner /> : 'Rodyti'}
        </button>
        {data && (
          <button onClick={handleCsv} className="secondary">
            CSV
          </button>
        )}
      </div>

      {error && <div className="error-message print:hidden">{error}</div>}

      {data && (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Dok. Nr.</th>
                <th>Turinys</th>
                <th style={{ textAlign: 'right' }}>Debetas</th>
                <th style={{ textAlign: 'right' }}>Kreditas</th>
                <th>Asmuo</th>
                <th>Korespondencijos</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.date}</td>
                  <td>{r.documentNumber}</td>
                  <td>{r.content}</td>
                  <td style={{ textAlign: 'right' }}>
                    <MoneyCell value={r.debit} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <MoneyCell value={r.credit} />
                  </td>
                  <td>{r.personName ?? ''}</td>
                  <td>{r.correspondence}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={7}>Periode įrašų sąskaitoje nėra</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={3}>
                  <strong>Likutis laikotarpio pradžiai:</strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.debitBalanceStart} />
                  </strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.creditBalanceStart} />
                  </strong>
                </td>
                <td colSpan={2}></td>
              </tr>
              <tr className="total-row">
                <td colSpan={3}>
                  <strong>Apyvarta per laikotarpį:</strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.debitTurnover} />
                  </strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.creditTurnover} />
                  </strong>
                </td>
                <td colSpan={2}></td>
              </tr>
              <tr className="total-row">
                <td colSpan={3}>
                  <strong>Likutis laikotarpio pabaigai:</strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.debitBalanceEnd} />
                  </strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.creditBalanceEnd} />
                  </strong>
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
